import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import { selectRoles, selectAftersalesRoles, selectIsAftersalesUser, selectManageRoles } from '../features/auth/authSlice';
import { selectUserParams } from '../store/slices/userParamsSlice';
import { Roles } from '../utils/roleEnums';
import { permissionChecker } from '../utils/permissionChecker';
import { usePermissionGrants } from './usePermissionGrants';

/**
 * Menü yetki kontrolü için custom hook
 */
export const usePermissions = () => {
    const userRoles = useSelector(selectRoles);
    const aftersalesRoles = useSelector(selectAftersalesRoles);
    const isAftersalesUser = useSelector(selectIsAftersalesUser);
    const userParams = useSelector(selectUserParams);
    const manageRoles = useSelector(selectManageRoles);
    const grants = usePermissionGrants();

    /**
     * Kullanıcının belirli bir role sahip olup olmadığını kontrol eder
     * @param {string} requiredRole - Gerekli rol
     * @returns {boolean}
     */
    const hasRole = useCallback(
        (requiredRole) => {
            if (!userRoles || !requiredRole) return false;
            // Case-insensitive role comparison to tolerate server-side casing differences
            const normalizedUserRoles = userRoles.map((r) => (typeof r === 'string' ? r.toLowerCase() : r));
            const normalizedRequired = typeof requiredRole === 'string' ? requiredRole.toLowerCase() : requiredRole;
            return normalizedUserRoles.includes(normalizedRequired) || userRoles.includes(requiredRole);
        },
        [userRoles]
    );

    /**
     * Kullanıcının belirtilen rollerden herhangi birine sahip olup olmadığını kontrol eder
     * @param {string[]} requiredRoles - Gerekli roller
     * @returns {boolean}
     */
    const hasAnyRole = useCallback(
        (requiredRoles) => {
            return userRoles && requiredRoles.some((role) => userRoles.includes(role));
        },
        [userRoles]
    );

    /**
     * Kullanıcının belirtilen tüm rollere sahip olup olmadığını kontrol eder
     * @param {string[]} requiredRoles - Gerekli roller
     * @returns {boolean}
     */
    const hasAllRoles = useCallback(
        (requiredRoles) => {
            return userRoles && requiredRoles.every((role) => userRoles.includes(role));
        },
        [userRoles]
    );

    /**
     * Aftersales rollerini kontrol eder
     * @param {string} requiredRole - Gerekli aftersales rolü
     * @returns {boolean}
     */
    const hasAftersalesRole = useCallback(
        (requiredRole) => {
            return aftersalesRoles && aftersalesRoles.includes(requiredRole);
        },
        [aftersalesRoles]
    );

    /**
     * Kullanıcının belirtilen manage (claim) iznine sahip olup olmadığını kontrol eder
     * manageRoles listesi üzerinden çalışır (rol bazlı DEĞİL). "Super Admin" yetkileri için kullanılır.
     * @param {string|string[]} requiredPermission - Gerekli manage izni (veya izinler)
     * @returns {boolean}
     */
    const hasManagePermission = useCallback(
        (requiredPermission) => {
            if (!requiredPermission || !Array.isArray(manageRoles)) return false;
            const required = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
            return required.some((perm) => manageRoles.includes(perm));
        },
        [manageRoles]
    );

    /**
     * Kullanıcının WARRANTY_TEAM rolüne sahip olup olmadığını kontrol eder
     * @returns {boolean}
     */
    const hasWarrantyTeamRole = useCallback(() => {
        return hasRole(Roles.WARRANTY_TEAM);
    }, [hasRole]);

    /**
     * Kullanıcının WARRANTY_MANAGER rolüne sahip olup olmadığını kontrol eder
     * @returns {boolean}
     */
    const hasWarrantyManagerRole = useCallback(() => {
        return hasRole(Roles.WARRANTY_MANAGER);
    }, [hasRole]);

    /**
     * Kullanıcının SSH_MANAGER rolüne sahip olup olmadığını kontrol eder
     * @returns {boolean}
     */
    const hasSSHManagerRole = useCallback(() => {
        return hasRole(Roles.SSH_MANAGER);
    }, [hasRole]);

    /**
     * Kullanıcının SSH_DIRECTOR rolüne sahip olup olmadığını kontrol eder
     * @returns {boolean}
     */
    const hasSSHDirectorRole = useCallback(() => {
        return hasRole(Roles.SSH_DIRECTOR);
    }, [hasRole]);

    /**
     * Kullanıcının SSH_GENERAL_MANAGER rolüne sahip olup olmadığını kontrol eder
     * @returns {boolean}
     */
    const hasSSHGeneralManagerRole = useCallback(() => {
        return hasRole(Roles.SSH_GENERAL_MANAGER);
    }, [hasRole]);

    /** Dealer role helper */
    const hasDealerRole = useCallback(() => {
        return hasRole(Roles.DEALER);
    }, [hasRole]);

    /** Service Dealer role helper */
    const hasServiceDealerRole = useCallback(() => {
        return hasRole(Roles.SERVICE_DEALER);
    }, [hasRole]);

    /** Sales Dealer role helper */
    const hasSalesDealerRole = useCallback(() => {
        return hasRole(Roles.SALES_DEALER);
    }, [hasRole]);

    /** Part Team role helper */
    const hasPartTeamRole = useCallback(() => {
        return hasRole(Roles.PART_TEAM);
    }, [hasRole]);

    /** Vehicle Sales Manager role helper */
    const hasVehicleSalesManagerRole = useCallback(() => {
        return hasRole(Roles.VEHICLE_SALES_MANAGER);
    }, [hasRole]);

    /** Warranty Quality Officer role helper */
    const hasWarrantyQualityOfficerRole = useCallback(() => {
        return hasRole(Roles.WARRANTY_QUALITY_OFFICER);
    }, [hasRole]);

    /** Technical Support User (Teknik Destek Kullanıcısı) role helper */
    const hasTechnicalSupportUserRole = useCallback(() => {
        return hasRole(Roles.TECHNICAL_SUPPORT_USER);
    }, [hasRole]);

    /** Karsan Logistics role helper */
    const hasKarsanLogisticsRole = useCallback(() => {
        return hasRole(Roles.KARSAN_LOGISTICS);
    }, [hasRole]);

    /** Karsan SSH role helper */
    const hasKarsanSSHRole = useCallback(() => {
        return hasRole(Roles.KARSAN_SSH);
    }, [hasRole]);

    /**
     * Kullanıcının ADMIN rolüne sahip olup olmadığını kontrol eder
     * @returns {boolean}
     */
    const hasAdminRole = useCallback(() => {
        return hasRole(Roles.ADMIN);
    }, [hasRole]);

    /**
     * Kullanıcının CEO rolüne sahip olup olmadığını kontrol eder
     * @returns {boolean}
     */
    const hasCEORole = useCallback(() => {
        return hasRole(Roles.CEO);
    }, [hasRole]);

    /** Accounting (Muhasebe) role helper */
    const hasAccountingRole = useCallback(() => {
        return hasRole(Roles.ACCOUNTING);
    }, [hasRole]);

    /**
     * Finance (Finans) role helper
     * Şimdilik tek/global bir finans yetkisi. İleride FinanceManager/FinanceEmployee gibi
     * rollere ayrılırsa bu helper ve FinanceTeamStrategy (permissionChecker.js) güncellenmeli.
     */
    const hasFinanceTeamRole = useCallback(() => {
        return hasRole(Roles.FINANCE_TEAM);
    }, [hasRole]);

    /**
     * Menü öğesinin görünür olup olmadığını kontrol eder
     * @param {Object} menuItem - Menü öğesi
     * @returns {boolean}
     */
    const canViewMenuItem = useCallback(
        (menuItem) => {
            // Eğer menü öğesinde permission tanımı yoksa varsayılan olarak görünür
            if (!menuItem.permissions) {
                return true;
            }

            const userData = {
                roles: userRoles,
                aftersalesRoles: aftersalesRoles,
                userParams: userParams,
                isAftersalesUser,
                manageRoles,
                grants
            };

            return permissionChecker.checkMenuItemPermission(userData, menuItem);
        },
        [userRoles, aftersalesRoles, userParams, isAftersalesUser, manageRoles, grants]
    );

    /**
     * Menü listesini filtreler
     * @param {Array} menuItems - Menü öğeleri listesi
     * @returns {Array} Filtrelenmiş menü öğeleri
     */
    const filterMenuItems = useCallback(
        (menuItems) => {
            if (!Array.isArray(menuItems)) {
                return [];
            }

            // Not: Dealer ve Admin bypass'ı kaldırıldı - tüm menü öğeleri canViewMenuItem kontrolünden geçmeli

            return menuItems
                .filter((item) => item.visible !== false) // visible: false olan öğeleri filtrele
                .filter((item) => canViewMenuItem(item))
                .map((item) => {
                    // Alt menü öğelerini de filtrele
                    if (item.children && item.children.length > 0) {
                        const filteredChildren = filterMenuItems(item.children);
                        return {
                            ...item,
                            children: filteredChildren
                        };
                    }
                    return item;
                })
                .filter((item) => {
                    // Eğer alt menü öğeleri varsa ve hepsi filtrelenmişse, ana öğeyi de gizle
                    if (item.children && item.children.length === 0) {
                        return false;
                    }
                    return true;
                });
        },
        [canViewMenuItem]
    );

    /**
     * Kullanıcının Onarım İş Emri oluşturma yetkisine sahip olup olmadığını kontrol eder
     * Dealer, ServiceDealer, SalesDealer, KarsanSSH ve WarrantyManager rolleri yeni iş emri oluşturabilir
     * @returns {boolean}
     */
    const canCreateCustomerDeclaration = useCallback(() => {
        return (
            hasAdminRole()||
            hasDealerRole() ||
            hasServiceDealerRole() ||
            hasSalesDealerRole() ||
            hasKarsanSSHRole() ||
            hasWarrantyTeamRole() ||
            hasWarrantyManagerRole()
        );
    }, [hasDealerRole, hasServiceDealerRole, hasSalesDealerRole, hasKarsanSSHRole, hasWarrantyManagerRole]);

    /**
     * Kullanıcının Teknik Destek Talebi oluşturma yetkisine sahip olup olmadığını kontrol eder
     * Admin ve tüm Bayi rolleri (Dealer, ServiceDealer, SalesDealer) oluşturabilir
     * @returns {boolean}
     */
    const canCreateTechnicalSupportRequest = useCallback(() => {
        return (
            hasAdminRole() ||
            hasDealerRole() ||
            hasServiceDealerRole() ||
            hasSalesDealerRole()
        );
    }, [hasAdminRole, hasDealerRole, hasServiceDealerRole, hasSalesDealerRole]);

    /**
     * Kullanıcının SADECE WarrantyManager rolüne sahip olup olmadığını kontrol eder
     * Eğer kullanıcının WarrantyManager dışında başka rolleri de varsa false döner
     * @returns {boolean}
     */
    const isOnlyWarrantyManager = useCallback(() => {
        if (!userRoles || userRoles.length === 0) return false;
        
        // Kullanıcının WarrantyManager rolü var mı kontrol et
        const hasWarrantyMgr = hasWarrantyManagerRole();
        if (!hasWarrantyMgr) return false;
        
        // Kullanıcının başka rolleri var mı kontrol et
        // Eğer sadece 1 rol varsa ve o da WarrantyManager ise true döner
        return userRoles.length === 1;
    }, [userRoles, hasWarrantyManagerRole]);

    return {
        hasRole,
        hasAnyRole,
        hasAllRoles,
        hasAftersalesRole,
        hasManagePermission,
        hasWarrantyTeamRole,
        hasWarrantyManagerRole,
        hasSSHManagerRole,
        hasSSHDirectorRole,
        hasSSHGeneralManagerRole,
        hasDealerRole,
        hasServiceDealerRole,
        hasSalesDealerRole,
        hasPartTeamRole,
        hasVehicleSalesManagerRole,
        hasWarrantyQualityOfficerRole,
        hasTechnicalSupportUserRole,
        hasKarsanLogisticsRole,
        hasKarsanSSHRole,
        hasAdminRole,
        hasCEORole,
        hasAccountingRole,
        hasFinanceTeamRole,
        canViewMenuItem,
        filterMenuItems,
        canCreateCustomerDeclaration,
        canCreateTechnicalSupportRequest,
        isOnlyWarrantyManager,
        userRoles,
        aftersalesRoles,
        manageRoles
    };
};
